import { Fab, LinkButton } from '@/components/elements/forms/buttons';
import { RatingV2 } from '@/components/elements/redesign/RatingV2/RatingV2';
import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED } from '@/constants/reviewConstants';

const baseTranslationKey = 'features.PlacePages.components.TabBar';

const TabBar = ({
  activeTab,
  top,
  placeId,
  placeName,
  reviews,
  onFavoriteClick,
  withEmployees,
  withReviews,
  className,
}: {
  top?: boolean;
  placeId?: number;
  placeName?: string;
  reviews?: { count: number; rating: number };
  withEmployees?: boolean;
  onFavoriteClick?: () => void;
  withReviews?: boolean;
  className?: string;
  activeTab: 'employees' | 'reviews' | 'about' | 'services';
}) => {
  const { isMobileView } = useMobileView();
  const user = useAppSelector((state) => state.users.user);
  const isFavorite = user ? user.favorites.indexOf(placeId) !== -1 : false;

  return (
    <div className={classnames('bg-surfaceColors-surface_l2', { [className]: !!className })}>
      <div className={`gap-xs flex h-full justify-between ${top ? 'w-[1040px]' : ''}`}>
        {top && !isMobileView && (
          <div className="gap-xxs flex min-w-0 flex-col justify-center">
            <div className="text-l truncate">{placeName}</div>
            {withReviews && (
              <RatingV2
                ratingCount={reviews?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED ? reviews?.count : 0}
                averageRating={reviews?.rating}
                size={'sm'}
                withAverageScore={reviews?.count >= MINIMUM_AMOUNT_OF_REVIEWS_REQUIRED}
                withRatingCounter
                withMultipleStars={false}
              />
            )}
          </div>
        )}
        <div className={`${isMobileView ? 'gap-lg' : 'gap-xl'} flex flex-shrink-0 flex-grow-0 items-center`}>
          {withEmployees ? (
            <a
              href={'#employees'}
              className={classnames('flex h-full outline-none', {
                'border-border_selected border-b-2': activeTab === 'employees',
                'border-b-2 border-white': activeTab !== 'employees',
              })}>
              <div className="gap-xs flex items-center">
                {!isMobileView && (
                  <Icon variant="users" color={activeTab === 'employees' ? 'fg_primary' : 'fg_secondary'} />
                )}
                <span className={`${activeTab === 'employees' ? 'text-fg_primary' : 'text-fg_secondary'}`}>
                  {_t(`${baseTranslationKey}.personal`)}
                </span>
              </div>
            </a>
          ) : null}
          {withReviews && (
            <a
              href={'#reviews'}
              className={classnames('flex h-full outline-none', {
                'border-border_selected border-b-2': activeTab === 'reviews',
                'border-b-2 border-white': activeTab !== 'reviews',
              })}>
              <div className="gap-xs flex items-center">
                {!isMobileView && (
                  <Icon variant="star-empty" color={activeTab === 'reviews' ? 'fg_primary' : 'fg_secondary'} />
                )}
                <span className={`${activeTab === 'reviews' ? 'text-fg_primary' : 'text-fg_secondary'}`}>
                  {_t(`${baseTranslationKey}.reviews`)}
                </span>
              </div>
            </a>
          )}
          <a
            href={'#about'}
            className={classnames('flex h-full outline-none', {
              'border-border_selected border-b-2': activeTab === 'about',
              'border-b-2 border-white': activeTab !== 'about',
            })}>
            <div className="gap-xs flex items-center">
              {!isMobileView && (
                <Icon variant="info-square" color={activeTab === 'about' ? 'fg_primary' : 'fg_secondary'} />
              )}
              <span className={`${activeTab === 'about' ? 'text-fg_primary' : 'text-fg_secondary'}`}>
                {_t(`${baseTranslationKey}.about`)}
              </span>
            </div>
          </a>
        </div>
        <div className="gap-xl ml-xs flex flex-shrink-0 items-center justify-end">
          {!isMobileView && (
            <Fab
              size="md"
              onClick={onFavoriteClick}
              aria-label={_t(`${baseTranslationKey}.favorites.${isFavorite ? 'remove' : 'add'}`)}
              variant="secondary"
              icon={<Icon variant={`heart${isFavorite ? '-filled' : ''}`} color="fg_primary" />}></Fab>
          )}
          <LinkButton
            size="md"
            to={'#services'}
            variant="primary"
            block={isMobileView}
            iconNoFilter
            label={_t(`${baseTranslationKey}.services`)}></LinkButton>
        </div>
      </div>
    </div>
  );
};

export default TabBar;
